import React from "react";
import { API_BASE_URL } from "@lib/config";

/**
 * Detect-and-ask confirmations for the capital-flows ledger (2026-09-01, CIO).
 *
 * The backend's daily detectors raise DRAFTS — a month with no attestation yet, a
 * probable wire arrival at Axos, a probable fee wire out of IBKR — and this card
 * presents them for one-click resolution. Confirming WRITES the ledger row
 * (attributed, audited, linked to the draft's evidence) and the return tile
 * self-heals within ~5 minutes. Dismissing records who and when, and the detector
 * will not re-ask the same condition.
 *
 * THE RULE THIS CARD ENFORCES BY EXISTING: nothing auto-writes the ledger. A row is
 * an attestation, and an attestation is a human standing behind it — this card is
 * that human's button. Amounts and dates are editable before confirming; the schema
 * has the final say (a refused row surfaces the reason, never a coerced value).
 *
 * Renders NOTHING when there is nothing to ask — the quiet state is silence, not an
 * empty box. Display/attestation only; nothing here trades.
 */

type Draft = {
  draft_id: number;
  kind: "MONTH_ATTEST" | "SUBSCRIPTION" | "FEE_PAYMENT" | string;
  suggested_effective_date: string;
  suggested_amount_usd: number | null;
  evidence: string;
  detected_at_utc: string | null;
};

const KIND_TITLE: Record<string, string> = {
  MONTH_ATTEST: "Month attestation needed",
  SUBSCRIPTION: "Probable wire arrival",
  FEE_PAYMENT: "Probable fee wire",
};

const AMBER = "rgb(180,118,8)";

export const LedgerConfirmCard: React.FC<{ className?: string }> = ({ className }) => {
  const [drafts, setDrafts] = React.useState<Draft[]>([]);
  const [busy, setBusy] = React.useState<number | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [edits, setEdits] = React.useState<Record<number, { amount?: string; date?: string; kind?: string }>>({});
  const actorRef = React.useRef<string>("");

  const load = React.useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/ledger/flow_drafts`);
      if (!res.ok) return;
      const json = await res.json();
      setDrafts(Array.isArray(json?.drafts) ? json.drafts : []);
    } catch {
      /* fail-soft: an unreachable draft feed renders nothing rather than noise */
    }
  }, []);

  React.useEffect(() => {
    load();
    // Signed-in identity from the SWA auth endpoint; unattributed fail-soft (the
    // backend logs that case). Local dev has no /.auth — expected, tolerated.
    (async () => {
      try {
        const res = await fetch("/.auth/me");
        if (res.ok) {
          const j = await res.json();
          actorRef.current = j?.clientPrincipal?.userDetails ?? "";
        }
      } catch {
        /* unattributed */
      }
    })();
  }, [load]);

  const resolve = async (d: Draft, action: "confirm" | "dismiss", overrides?: { kind?: string }) => {
    setBusy(d.draft_id);
    setError(null);
    try {
      const e = edits[d.draft_id] ?? {};
      const body: Record<string, unknown> = { action, actor: actorRef.current };
      if (action === "confirm") {
        if (overrides?.kind) body.kind = overrides.kind;
        else if (e.kind) body.kind = e.kind;
        if (e.date) body.effective_date = e.date;
        if (e.amount !== undefined && e.amount !== "" && (overrides?.kind ?? e.kind) !== "NONE") {
          body.amount_usd = Number(e.amount.replace(/[,$\s]/g, ""));
        }
      }
      const res = await fetch(`${API_BASE_URL}/ledger/flow_drafts/${d.draft_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(String(json?.error ?? `HTTP ${res.status}`));
      } else if (action === "confirm") {
        setNotice("Recorded — the return tile re-reads the ledger within ~5 minutes.");
      }
      await load();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  };

  if (!drafts.length && !notice) return null;

  const inputStyle: React.CSSProperties = {
    background: "transparent",
    border: "1px solid var(--card-border)",
    borderRadius: 6,
    padding: "2px 6px",
    color: "var(--text-primary)",
    fontVariantNumeric: "tabular-nums",
    width: 110,
  };
  const btn = (primary: boolean): React.CSSProperties => ({
    border: `1px solid ${primary ? AMBER : "var(--card-border)"}`,
    color: primary ? AMBER : "var(--text-secondary)",
    borderRadius: 6,
    padding: "2px 10px",
    fontWeight: primary ? 600 : 400,
  });

  return (
    <div
      className={className}
      style={{ border: `1px solid ${AMBER}`, borderRadius: 12, padding: "10px 12px" }}
    >
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: AMBER }}>
        Flow ledger — needs your confirmation
      </div>
      {notice ? (
        <div className="mt-1 text-[11px]" style={{ color: "var(--text-secondary)" }}>
          {notice}
        </div>
      ) : null}
      {error ? (
        <div className="mt-1 text-[11px]" style={{ color: AMBER }}>
          {error}
        </div>
      ) : null}
      {drafts.map((d) => {
        const e = edits[d.draft_id] ?? {};
        const setEdit = (patch: Partial<{ amount: string; date: string; kind: string }>) =>
          setEdits((prev) => ({ ...prev, [d.draft_id]: { ...prev[d.draft_id], ...patch } }));
        const isMonth = d.kind === "MONTH_ATTEST";
        return (
          <div key={d.draft_id} className="mt-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
            <div style={{ color: "var(--text-primary)", fontWeight: 600 }}>
              {KIND_TITLE[d.kind] ?? d.kind}
            </div>
            <div className="mt-0.5 leading-5">{d.evidence}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {isMonth ? (
                <>
                  <button
                    type="button"
                    disabled={busy === d.draft_id}
                    style={btn(true)}
                    onClick={() => resolve(d, "confirm", { kind: "NONE" })}
                  >
                    No flows this month
                  </button>
                  <span style={{ opacity: 0.7 }}>or</span>
                  <select
                    value={e.kind ?? "SUBSCRIPTION"}
                    onChange={(ev) => setEdit({ kind: ev.target.value })}
                    style={{ ...inputStyle, width: 130 }}
                  >
                    <option value="SUBSCRIPTION">Subscription</option>
                    <option value="REDEMPTION">Redemption</option>
                  </select>
                  <input
                    placeholder="amount $"
                    value={e.amount ?? ""}
                    onChange={(ev) => setEdit({ amount: ev.target.value })}
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    disabled={busy === d.draft_id || !(e.amount ?? "").trim()}
                    style={btn(true)}
                    onClick={() => resolve(d, "confirm", { kind: e.kind ?? "SUBSCRIPTION" })}
                  >
                    Record flow
                  </button>
                </>
              ) : (
                <>
                  <input
                    value={e.amount ?? (d.suggested_amount_usd != null ? String(Math.abs(d.suggested_amount_usd)) : "")}
                    onChange={(ev) => setEdit({ amount: ev.target.value })}
                    style={inputStyle}
                    aria-label="amount USD"
                  />
                  <input
                    value={e.date ?? d.suggested_effective_date}
                    onChange={(ev) => setEdit({ date: ev.target.value })}
                    style={{ ...inputStyle, width: 120 }}
                    aria-label="effective date"
                  />
                  <button
                    type="button"
                    disabled={busy === d.draft_id}
                    style={btn(true)}
                    onClick={() => resolve(d, "confirm")}
                  >
                    Confirm {d.kind === "FEE_PAYMENT" ? "fee wire" : "subscription"}
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={busy === d.draft_id}
                style={btn(false)}
                onClick={() => resolve(d, "dismiss")}
              >
                Dismiss
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LedgerConfirmCard;
